import { WEEKDAYS, WEEKDAY_LABELS, type TimeInterval, type Weekday, type WeeklyHours } from '@lia/contracts';
import { Alert02Icon, Copy01Icon, Delete02Icon, MoonIcon, PlusSignIcon } from '@hugeicons/core-free-icons';
import { Icon } from '../primitives/icon.js';
import { cn } from '../../lib/cn.js';
import { StatusBadge } from '../primitives/badge.js';
import { Button, IconButton } from '../primitives/button.js';
import { TimeSelect } from '../primitives/date-time-field.js';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from '../primitives/dropdown-menu.js';

/** An interval whose end is earlier than or equal to its start continues into the next day ("24:00" = end of day). */
export function isOvernight(i: TimeInterval): boolean {
  return i.end !== '24:00' && i.end <= i.start;
}

function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

function fromMinutes(total: number): string {
  if (total >= 24 * 60) return '24:00';
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

/** True when two same-day (non-overnight) intervals overlap. */
export function hasOverlap(intervals: readonly TimeInterval[]): boolean {
  const sameDay = intervals.filter((i) => !isOvernight(i)).map((i) => [toMinutes(i.start), toMinutes(i.end)] as const);
  sameDay.sort((a, b) => a[0] - b[0]);
  for (let k = 1; k < sameDay.length; k += 1) {
    const prev = sameDay[k - 1];
    const cur = sameDay[k];
    if (prev && cur && cur[0] < prev[1]) return true;
  }
  return false;
}

function nextInterval(existing: readonly TimeInterval[]): TimeInterval {
  const last = existing[existing.length - 1];
  if (!last) return { start: '09:00', end: '19:00' };
  const start = last.end === '24:00' || isOvernight(last) ? '09:00' : last.end;
  return { start, end: fromMinutes(Math.min(24 * 60, toMinutes(start) + 120)) };
}

export interface TimeRangeEditorProps {
  value: TimeInterval;
  onChange: (value: TimeInterval) => void;
  onRemove?: () => void;
  /** Context for accessible names, e.g. "Lunes, horario 1". */
  label: string;
  stepMinutes?: number;
  disabled?: boolean;
  className?: string;
}

/** One HH:MM – HH:MM interval with an explicit "continúa al día siguiente" flag for overnight ranges. */
export function TimeRangeEditor({ value, onChange, onRemove, label, stepMinutes = 30, disabled, className }: TimeRangeEditorProps) {
  const overnight = isOvernight(value);
  return (
    <div role="group" aria-label={label} className={cn('flex flex-wrap items-center gap-2', className)}>
      <TimeSelect
        aria-label={`Inicio, ${label}`}
        className="w-[6.5rem]"
        value={value.start}
        stepMinutes={stepMinutes}
        disabled={disabled}
        onValueChange={(start) => onChange({ ...value, start })}
      />
      <span aria-hidden className="text-text-secondary">
        –
      </span>
      <TimeSelect
        aria-label={`Fin, ${label}`}
        className="w-[6.5rem]"
        value={value.end}
        stepMinutes={stepMinutes}
        allowEndOfDay
        disabled={disabled}
        onValueChange={(end) => onChange({ ...value, end })}
      />
      {overnight ? (
        <StatusBadge tone="info" icon={<Icon icon={MoonIcon} aria-hidden />}>
          Continúa al día siguiente
        </StatusBadge>
      ) : null}
      {onRemove ? <IconButton size="sm" label={`Quitar ${label}`} icon={<Icon icon={Delete02Icon} aria-hidden />} onClick={onRemove} disabled={disabled} /> : null}
    </div>
  );
}

export interface WeeklyScheduleEditorProps {
  value: WeeklyHours;
  onChange: (value: WeeklyHours) => void;
  disabled?: boolean;
  /** Max intervals per day (contract allows 6). */
  maxIntervals?: number;
  stepMinutes?: number;
  className?: string;
}

const WORKDAYS: readonly Weekday[] = ['mon', 'tue', 'wed', 'thu', 'fri'];

/**
 * Weekly operating hours: per weekday list of intervals (split shifts allowed, overnight intervals flagged),
 * add / remove intervals and copy one day to weekdays or all days. Value is `WeeklyHours` from @lia/contracts.
 */
export function WeeklyScheduleEditor({ value, onChange, disabled, maxIntervals = 6, stepMinutes = 30, className }: WeeklyScheduleEditorProps) {
  const setDay = (day: Weekday, intervals: TimeInterval[]) => onChange({ ...value, [day]: intervals });
  const copy = (from: Weekday, to: readonly Weekday[]) => {
    const next = { ...value };
    for (const d of to) if (d !== from) next[d] = value[from].map((i) => ({ ...i }));
    onChange(next);
  };
  return (
    <div className={cn('grid divide-y divide-border-subtle rounded-card border border-border-subtle bg-surface-default', className)}>
      {WEEKDAYS.map((day) => {
        const intervals = value[day];
        const dayLabel = WEEKDAY_LABELS[day];
        const overlap = hasOverlap(intervals);
        return (
          <div key={day} role="group" aria-label={dayLabel} className="grid gap-2 px-4 py-3 md:grid-cols-[8rem_minmax(0,1fr)_auto] md:items-start">
            <span className="pt-2 text-body font-medium text-text-primary">{dayLabel}</span>
            <div className="grid gap-2">
              {intervals.length === 0 ? <span className="pt-2 text-body text-text-secondary">Cerrado</span> : null}
              {intervals.map((interval, i) => (
                <TimeRangeEditor
                  key={i}
                  label={`${dayLabel}, horario ${i + 1}`}
                  value={interval}
                  stepMinutes={stepMinutes}
                  disabled={disabled}
                  onChange={(v) => setDay(day, intervals.map((x, k) => (k === i ? v : x)))}
                  onRemove={() => setDay(day, intervals.filter((_, k) => k !== i))}
                />
              ))}
              {overlap ? (
                <p className="flex items-center gap-1.5 text-meta font-medium text-warning">
                  <Icon icon={Alert02Icon} aria-hidden className="size-3.5" />
                  Los horarios de este día se traslapan.
                </p>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-1 md:justify-end">
              <Button
                variant="ghost"
                size="sm"
                leadingIcon={<Icon icon={PlusSignIcon} aria-hidden />}
                disabled={disabled || intervals.length >= maxIntervals}
                onClick={() => setDay(day, [...intervals, nextInterval(intervals)])}
                aria-label={`Agregar horario el ${dayLabel.toLowerCase()}`}
              >
                Agregar horario
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild disabled={disabled}>
                  <IconButton size="sm" label={`Copiar horario del ${dayLabel.toLowerCase()}`} icon={<Icon icon={Copy01Icon} aria-hidden />} />
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuLabel>Copiar horario del {dayLabel.toLowerCase()} a…</DropdownMenuLabel>
                  <DropdownMenuItem onSelect={() => copy(day, WORKDAYS)}>Lunes a viernes</DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => copy(day, WEEKDAYS)}>Todos los días</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        );
      })}
    </div>
  );
}
